import React, { useState, useEffect } from "react";
import WidgetContainer from "../WidgetContainer.jsx";
import pandaImg from "../../../../../assets/panda_stakeholders.png";
import StakeholderSelectionScreen from "./stakeholderValuesAndInterests/StakeholderSelectionScreen.jsx";
import ProfileStep1Adapter from "./stakeholderValuesAndInterests/ProfileStep1Adapter.jsx";
import ProfileStep2Adapter from "./stakeholderValuesAndInterests/ProfileStep2Adapter.jsx";
import StakeholderRelationRadar from "./stakeholderValuesAndInterests/StakeholderRelationRadar.jsx";
import StakeholderRadar2Intro from "./stakeholderValuesAndInterests/StakeholderRadar2Intro.jsx";
import StakeholderRelationRadar2 from "./stakeholderValuesAndInterests/StakeholderRelationRadar2.jsx";
import { progressService } from "../../../../../services/progressService";

const StakeholderSelectionScreenWrapper = ({
  userId,
  subsectionId,
  onNextStep,
  onPreviousStep,
  onPhaseChange,
  initialSelectedStakeholders,
  onStakeholderSelection,
}) => {
  const [availableStakeholders, setAvailableStakeholders] = useState({
    boss: null,
    others: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (onPhaseChange) {
      onPhaseChange("intro");
    }
    return () => {
      if (onPhaseChange) {
        onPhaseChange("immersive");
      }
    };
  }, [onPhaseChange]);

  // ✅ FETCH STAKEHOLDERS FROM BACKEND
  useEffect(() => {
    const fetchStakeholders = async () => {
      try {
        setIsLoading(true);

        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "interpersonal",
          "interpersonal_map"
        );

        const visibilityMap = res?.visibilityMap || {};
        console.log("🔍 RAW Backend Data:", JSON.stringify(visibilityMap, null, 2));
        
        // ✅ STRICT: ONLY "Boss (Direct Manager)" 
        // Will match variations like:
        // - "Boss (Direct Manager)"
        // - "boss (direct manager)" 
        // - "BOSS (DIRECT MANAGER)"
        // - "Boss(Direct Manager)" (no space)
        const isDirectBoss = (role) => {
          if (!role) {
            console.log(`  ✗ Role is null/undefined`);
            return false;
          }
          
          // Remove extra spaces and make lowercase for comparison
          const normalized = role.toLowerCase().replace(/\s+/g, ' ').trim();
          
          // Check for exact match
          const isMatch = normalized === "boss (direct manager)" || 
                         normalized === "boss(direct manager)" ||
                         normalized === "direct manager" ||
                         normalized === "boss";
          
          if (isMatch) {
            console.log(`  ✅ DIRECT BOSS FOUND: "${role}"`);
          } else {
            console.log(`  ✗ NOT Direct Boss: "${role}"`);
          }
          
          return isMatch;
        };

        // 🔥 Step 1: Scan ALL quadrants to find "Boss (Direct Manager)" ONLY
        const allQuadrants = ["q1", "q2", "q3", "q4"];
        let bossStakeholder = null;
        
        console.log("🔍 Scanning ALL quadrants for 'Boss (Direct Manager)'...");
        
        for (const qKey of allQuadrants) {
          const items = visibilityMap[qKey]?.items || [];
          console.log(`  → Checking ${qKey.toUpperCase()}: ${items.length} items`);
          
          for (const s of items) {
            if (s.name && s.relation) {
              console.log(`    Checking: ${s.name} - Role: "${s.relation}"`);
              
              if (isDirectBoss(s.relation)) {
                // ✅ DIRECT BOSS FOUND!
                bossStakeholder = {
                  id: s.id || `boss-${Math.random().toString(36).substr(2, 9)}`,
                  name: s.name,
                  role: s.relation,
                  avatar: s.animal || s.image || "👔",
                  color: "purple",
                  quadrant: qKey,
                };
                console.log(`✅ ✅ ✅ BOSS (DIRECT MANAGER) FOUND in ${qKey.toUpperCase()}!`);
                console.log(`    Name: ${bossStakeholder.name}`);
                console.log(`    Role: ${bossStakeholder.role}`);
                console.log(`    ID: ${bossStakeholder.id}`);
                break;
              }
            }
          }
          
          if (bossStakeholder) break;
        }

        if (!bossStakeholder) {
          console.error("❌ ❌ ❌ NO 'Boss (Direct Manager)' FOUND in ANY quadrant!");
          console.error("Please add a stakeholder with role = 'Boss (Direct Manager)' in Interpersonal Map");
        }

        // 🔥 Step 2: Get Q1 stakeholders ONLY (excluding direct boss)
        const q1Items = visibilityMap.q1?.items || [];
        const otherStakeholders = [];
        
        console.log("");
        console.log("🔍 Processing Q1 stakeholders (excluding Direct Boss):");
        console.log(`  → Q1 has ${q1Items.length} total items`);
        
        for (const s of q1Items) {
          if (s.name && s.relation) {
            const isThisDirectBoss = isDirectBoss(s.relation);
            console.log(`  • ${s.name} (${s.relation}): isDirectBoss = ${isThisDirectBoss}`);
            
            // ✅ Add to Q1 list ONLY if NOT direct boss
            if (!isThisDirectBoss) {
              const stakeholder = {
                id: s.id || `q1-${Math.random().toString(36).substr(2, 9)}`,
                name: s.name,
                role: s.relation,
                avatar: s.animal || s.image || "🦁",
                color: "orange",
              };
              otherStakeholders.push(stakeholder);
              console.log(`    ✓ Added to Q1 list:`, stakeholder.name);
            } else {
              console.log(`    ✗ Excluded (is Direct Boss):`, s.name);
            }
          }
        }

        console.log("");
        console.log("📊 ========== FINAL RESULTS ==========");
        console.log("STAKEHOLDER 1 (Direct Boss ONLY - LOCKED):");
        if (bossStakeholder) {
          console.log("  ├─ ✅ FOUND!");
          console.log("  ├─ Name:", bossStakeholder.name);
          console.log("  ├─ Role:", bossStakeholder.role);
          console.log("  └─ From Quadrant:", bossStakeholder.quadrant);
        } else {
          console.log("  └─ ❌ NOT FOUND - Add 'Boss (Direct Manager)' to Interpersonal Map");
        }
        console.log("");
        console.log("STAKEHOLDER 2 (Q1 Others - DROPDOWN):");
        console.log("  ├─ Total Count:", otherStakeholders.length);
        if (otherStakeholders.length > 0) {
          otherStakeholders.forEach((s, i) => {
            console.log(`  ├─ [${i + 1}] ${s.name} (${s.role})`);
          });
        } else {
          console.log("  └─ No Q1 stakeholders available");
        }
        console.log("====================================");

        setAvailableStakeholders({
          boss: bossStakeholder,
          others: otherStakeholders,
        });
      } catch (err) {
        console.error("❌ Error fetching stakeholders:", err);
        setAvailableStakeholders({ boss: null, others: [] });
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchStakeholders();
    }
  }, [userId]);

  const handleSelectionComplete = (selectedStakeholders) => {
    if (typeof onStakeholderSelection === "function") {
      onStakeholderSelection(selectedStakeholders);
    }
    if (typeof onNextStep === "function") {
      onNextStep();
    }
  };

  return (
    <StakeholderSelectionScreen
      userId={userId}
      subsectionId={subsectionId}
      onComplete={handleSelectionComplete}
      initialStakeholders={initialSelectedStakeholders}
      bossStakeholder={availableStakeholders.boss}
      otherStakeholders={availableStakeholders.others}
      isLoading={isLoading}
      onPreviousStep={onPreviousStep}
    />
  );
};

const StakeholderValuesContainer = ({
  userId,
  subsectionId,
  onComplete,
  onPhaseChange,
}) => {
  const [selectedStakeholders, setSelectedStakeholders] = useState([null, null]);

  useEffect(() => {
    const loadInitialSelection = async () => {
      if (!userId || !subsectionId) return;

      try {
        const res = await progressService.getUserProgress(userId, subsectionId);
        const payload = res?.data?.payload || res?.payload || null;
        if (payload?.selectedStakeholders) {
          console.log("📥 [Container] Loaded selected stakeholders:", payload.selectedStakeholders);
          setSelectedStakeholders(payload.selectedStakeholders);
        }
      } catch (e) {
        console.log(
          "[StakeholderValuesContainer] No existing selection or failed to load",
          e
        );
      }
    };

    loadInitialSelection();
  }, [userId, subsectionId]);

  return (
    <WidgetContainer
      userId={userId}
      subsectionId={subsectionId}
      onComplete={onComplete}
      onPhaseChange={onPhaseChange}
      intro={{
        image: pandaImg,
        title: "Stakeholder Values & Interests",
        description:
          "Let's explore what your boss truly values by identifying their interests, promotion KPIs, and core values. This will help you align better, build trust, and work more effectively with them. We'll also look at one other key stakeholder who is essential for your promotion.",
        buttonLabel: "Get Started",
      }}
      steps={[
        {
          id: "selection",
          title: "Stakeholder Values & Interests",
          component: (props) => (
            <StakeholderSelectionScreenWrapper
              {...props}
              userId={userId}
              subsectionId={subsectionId}
              onPhaseChange={onPhaseChange}
              initialSelectedStakeholders={selectedStakeholders}
              onStakeholderSelection={(selected) => {
                console.log("📤 [Container] Setting selected stakeholders:", selected);
                setSelectedStakeholders(selected);
              }}
            />
          ),
        },
        {
          id: "profile1",
          // title: "Profile Stakeholder 1",
          title: selectedStakeholders?.[0]?.name
            ? `Profile ${selectedStakeholders[0].name}`
            : "Profile Stakeholder 1",
          component: (props) => (
            <ProfileStep1Adapter
              {...props}
              userId={userId}
              subsectionId={subsectionId}
              onPhaseChange={onPhaseChange}
              initialSelectedStakeholders={selectedStakeholders}
            />
          ),
        },
        {
          id: "radar",
          title: "Visibility Radar",
          component: (props) => (
            <StakeholderRelationRadar
              {...props}
              userId={userId}
              subsectionId={subsectionId}
            />
          ),
          fullScreen: true,
        },
        {
          id: "radar2-intro",
          title: "Stakeholder 2 Intro",
          component: (props) => (
            <StakeholderRadar2Intro
              {...props}
              userId={userId}
              subsectionId={subsectionId}
            />
          ),
          fullScreen: true,
        },
        {
          id: "profile2",
          // title: "Profile Stakeholder 2",
          title: selectedStakeholders?.[1]?.name
            ? `Profile ${selectedStakeholders[1].name}`
            : "Profile Stakeholder 2",
          component: (props) => (
            <ProfileStep2Adapter
              {...props}
              userId={userId}
              subsectionId={subsectionId}
              onPhaseChange={onPhaseChange}
              initialSelectedStakeholders={selectedStakeholders}
            />
          ),
        },
        {
          id: "radar2",
          title: "Stakeholder 2 Radar",
          component: (props) => (
            <StakeholderRelationRadar2
              {...props}
              userId={userId}
              subsectionId={subsectionId}
              onNextStep={async () => {
                try {
                  console.log("🎯 [Container] Final step completed - saving as 'completed'");
                  
                  // Load existing payload
                  const res = await progressService.getUserProgress(userId, subsectionId);
                  const payload = res?.data?.payload || res?.payload || {};
                  
                  console.log("📦 [Container] Current payload:", payload);
                  
                  // ✅ CRITICAL: Save with "completed" status
                  await progressService.saveProgress(
                    userId,
                    subsectionId,
                    {
                      type: "widget",
                      payload: payload,
                    },
                    "completed" // ✅ This marks the module as completed
                  );
                  
                  console.log("✅ [Container] Successfully saved as completed");
                  
                  // Call the parent onComplete
                  if (typeof onComplete === "function") {
                    onComplete();
                  }
                } catch (err) {
                  console.error("❌ [Container] Failed to mark subsection as completed:", err);
                  
                  // Still call onComplete even if save fails
                  if (typeof onComplete === "function") {
                    onComplete();
                  }
                }
              }}
            />
          ),
          fullScreen: true,
        },
      ]}
    />
  );
};

export default StakeholderValuesContainer;
